import { useMemo, useState, useEffect } from 'react';
import {
  Key, ArrowLeft, Save, Trash2, Loader2, Lock, AlertTriangle,
} from 'lucide-react';
import {
  fetchAccessList, fetchAccessDetail,
  updateAccessAssignment, deleteAccessAssignment, getDisplayErrorMessage,
  type AccessSummary, type AccessDetailView,
} from '@/lib/api';
import { ScopePicker } from '@/components/access/ScopePicker';
import { withChargerLocations } from '@/lib/scopeSelection';
import formStyles from '@/components/ui/Form.module.css';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { useAuthStore } from '@/store/auth';
import { roleNameForDisplay } from '@/pages/users/roleLabels';
import styles from './AccessPage.module.css';

interface AccessPageProps {
  onNavigate?: (path: string) => void;
}

export function AccessPage({ onNavigate }: AccessPageProps) {
  useRecentRoute('Access Management');
  const [list, setList] = useState<AccessSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const load = async (): Promise<void> => {
    setLoading(true);
    setError('');
    try {
      setList(await fetchAccessList());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  // ── Hooks must be called unconditionally, BEFORE any
  //    early return, or React's hook-order tracker throws
  //    "Rendered fewer hooks than expected" on the render
  //    where the early return fires. The two useMemo calls
  //    below previously sat under the `if (selectedUserId)`
  //    early return, which silently broke when a row was
  //    clicked: first render had 7 hooks, second render had
  //    only the 5 above the early return → React threw and
  //    the root unmounted (caught by the AppShell
  //    ErrorBoundary). Keep all hooks above any conditional
  //    return forever.
  //
  // Filter + sort. Sort precedence (Phase B): rows that need
  // scope (third_party_admin without buildings) bubble to the
  // top, then implicit-all-access rows (super_admin /
  // tenant_admin) so admins see "this is configured" rows after
  // the actionable ones, then everyone else alphabetically by
  // name. Fix-the-issue UX leads; the rest of the list is
  // organized for scanning.
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const matches = q
      ? list.filter((row) =>
          row.user_name.toLowerCase().includes(q)
          || row.user_email.toLowerCase().includes(q),
        )
      : list;
    const tier = (r: AccessSummary): number => {
      if (r.needs_scope) return 0;
      if (r.has_implicit_all_access) return 1;
      return 2;
    };
    return [...matches].sort((a, b) => {
      const ta = tier(a);
      const tb = tier(b);
      if (ta !== tb) return ta - tb;
      return a.user_name.localeCompare(b.user_name);
    });
  }, [list, search]);

  const needsScopeCount = useMemo(
    () => list.filter((r) => r.needs_scope).length,
    [list],
  );

  // Conditional return AFTER all hooks have been called. When
  // a row is clicked the AccessDetail view replaces the list,
  // but every hook above already ran on this render so React's
  // tracker stays consistent with the prior render's hook count.
  if (selectedUserId) {
    return (
      <AccessDetail
        userId={selectedUserId}
        onBack={() => { setSelectedUserId(null); void load(); }}
      />
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><Key size={18} /></div>
          <div>
            <h1 className={styles.title}>Access Management</h1>
            <p className={styles.subtitle}>
              {list.length} user{list.length === 1 ? '' : 's'} with role assignments
              {needsScopeCount > 0 && (
                <>
                  {' · '}
                  <span style={{ color: 'var(--color-warn)', fontWeight: 600 }}>
                    {needsScopeCount} need{needsScopeCount === 1 ? 's' : ''} scope
                  </span>
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      <div className={styles.filterRow}>
        <input
          className={styles.search}
          type="text"
          placeholder="Search by name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading…</div>
      ) : (
        <div className={styles.list}>
          {filtered.map((row) => (
            <button
              key={row.user_id}
              type="button"
              className={styles.row}
              onClick={() => setSelectedUserId(row.user_id)}
            >
              <div className={styles.rowMain}>
                <span className={styles.rowName}>{row.user_name}</span>
                <span className={styles.rowEmail}>{row.user_email}</span>
              </div>
              <div className={styles.rowMid}>
                {row.role_name && (
                  <span className={styles.roleBadge}>{roleNameForDisplay(row.role_name)}</span>
                )}
              </div>
              <div className={styles.rowAside}>
                {row.needs_scope ? (
                  <span
                    className={styles.needsScopeBadge}
                    title="This admin has a building-admin role but no scope assigned. Click to assign locations."
                  >
                    <AlertTriangle size={11} /> Needs scope
                  </span>
                ) : row.has_implicit_all_access ? (
                  <span className={styles.allAccessBadge}>
                    <Lock size={11} /> All access
                  </span>
                ) : (
                  <span className={styles.scopeSummary}>
                    {row.location_ids.length} station{row.location_ids.length === 1 ? '' : 's'}
                    {' · '}
                    {row.charger_ids.length} charger{row.charger_ids.length === 1 ? '' : 's'}
                  </span>
                )}
              </div>
            </button>
          ))}
          {filtered.length === 0 && !loading && (
            <p className={styles.emptyState}>
              {search
                ? 'No users match that search.'
                : 'No users with role assignments yet. Invite a user from the Users page.'}
            </p>
          )}
        </div>
      )}

      <p className={styles.unused}>{onNavigate ? '' : ''}</p>
    </div>
  );
}

// ── Detail editor ───────────────────────────────────────────────────

interface AccessDetailProps {
  userId: string;
  onBack: () => void;
}

function AccessDetail({ userId, onBack }: AccessDetailProps) {
  // Changing someone's access is the tenant admin's (F4 #11, 2026-09-25):
  // PUT and DELETE /admin/access/:userId refuse everyone else, operators
  // included. Mirror it, so an operator who reaches this page by URL reads
  // the scope instead of pressing a Save that 403s. A templated persona's
  // tenant_admin claim does not count (same mirror as MoneyPage).
  const user = useAuthStore((st) => st.user);
  const canEdit = user?.role === 'super_admin'
    || (user?.role === 'tenant_admin' && !user?.template);
  const [view, setView] = useState<AccessDetailView | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [draftLocations, setDraftLocations] = useState<Set<string>>(new Set());
  const [draftChargers, setDraftChargers] = useState<Set<string>>(new Set());
  // Stations added on load because the saved grant held chargers without
  // them (an older grant; the picker can no longer build one).
  const [repairedStations, setRepairedStations] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError('');
    fetchAccessDetail(userId)
      .then((v) => {
        setView(v);
        const saved = new Set(v.location_ids);
        // Repair only for someone who can save it, and only with stations
        // the picker lists: a read-only viewer sees the grant as stored.
        const needed = canEdit
          ? withChargerLocations(
            saved, new Set(v.charger_ids), v.available_chargers,
            new Set(v.available_locations.map((l) => l._id)),
          )
          : saved;
        setDraftLocations(new Set(needed));
        setRepairedStations(needed.size - saved.size);
        setDraftChargers(new Set(v.charger_ids));
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [userId, canEdit]);

  const handleSave = async (): Promise<void> => {
    if (!view) return;
    setSaving(true);
    setError('');
    try {
      await updateAccessAssignment(userId, {
        location_ids: [...draftLocations],
        charger_ids: [...draftChargers],
      });
      onBack();
    } catch (err) {
      // The server's own words (e.g. CHARGER_LOCATION_NOT_GRANTED names the
      // charger), not ky's generic "Request failed with status code 400".
      setError(await getDisplayErrorMessage(err, 'Save failed'));
      setSaving(false);
    }
  };

  const handleClearAccess = async (): Promise<void> => {
    if (!confirm('Remove all access for this user? They will lose visibility into every station + charger until you reassign.')) {
      return;
    }
    setSaving(true);
    try {
      await deleteAccessAssignment(userId);
      onBack();
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Delete failed'));
      setSaving(false);
    }
  };

  // Toggle handlers + select-all/clear shortcuts moved into the
  // shared ScopePicker component (commit Phase B #4 extraction).

  if (loading) {
    return (
      <div className={styles.page}>
        <button className={styles.backBtn} onClick={onBack}>
          <ArrowLeft size={16} /> Back to Access Management
        </button>
        <div className={styles.loading}>Loading…</div>
      </div>
    );
  }

  if (!view) {
    return (
      <div className={styles.page}>
        <button className={styles.backBtn} onClick={onBack}>
          <ArrowLeft size={16} /> Back to Access Management
        </button>
        <div className={styles.error}>{error || 'User not found'}</div>
      </div>
    );
  }

  // Charger-by-location grouping moved into ScopePicker.

  return (
    <div className={styles.page}>
      <button className={styles.backBtn} onClick={onBack}>
        <ArrowLeft size={16} /> Back to Access Management
      </button>

      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><Key size={18} /></div>
          <div>
            <h1 className={styles.title}>{view.user.name}</h1>
            <p className={styles.subtitle}>
              {view.user.email}
              {view.role && (
                <>{' · '}<span className={styles.roleInline}>{roleNameForDisplay(view.role.name)}</span></>
              )}
            </p>
          </div>
        </div>
        {view.has_implicit_all_access ? (
          <span className={styles.allAccessBadge}>
            <Lock size={12} /> All access · scope not configurable
          </span>
        ) : canEdit ? (
          <div className={styles.detailActions}>
            <button
              type="button"
              className={styles.dangerBtn}
              onClick={handleClearAccess}
              disabled={saving}
            >
              <Trash2 size={13} /> Clear access
            </button>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <><Loader2 size={13} className={styles.spin} /> Saving…</> : <><Save size={13} /> Save</>}
            </button>
          </div>
        ) : (
          <span className={styles.allAccessBadge}>
            <Lock size={12} /> Only a tenant admin can change access
          </span>
        )}
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {view.has_implicit_all_access ? (
        <div className={styles.allAccessNote}>
          This role grants implicit all-access within the tenant.
          Scope-level (per-Station, per-Charger) restrictions don&rsquo;t apply.
          Edit the user&rsquo;s role on the Users page if you need to narrow
          their access.
        </div>
      ) : (
        <>
        {repairedStations > 0 && (
          <div className={styles.allAccessNote}>
            {repairedStations === 1 ? 'One station was' : `${repairedStations} stations were`} added
            because this user already had chargers there: a charger is only
            visible with its station. Save to keep the change.
          </div>
        )}
        <ScopePicker
          availableLocations={view.available_locations}
          availableChargers={view.available_chargers}
          selectedLocations={draftLocations}
          selectedChargers={draftChargers}
          onLocationsChange={setDraftLocations}
          onChargersChange={setDraftChargers}
          disabled={saving || !canEdit}
          showSummaryFooter
          intro={
            <p>
              Pick the stations and chargers this user can see. Selecting a
              station alone doesn&rsquo;t grant charger access; toggle the
              specific chargers within. Ticking a charger adds its station,
              and unticking a station clears its chargers. Use the
              per-station shortcuts to select or clear all chargers at once.
            </p>
          }
        />
        </>
      )}

      {/* Reuse formStyles to keep button shapes consistent across pages. */}
      <p className={styles.unused}>{formStyles ? '' : ''}</p>
    </div>
  );
}
